import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import useDeepCompareEffect from 'use-deep-compare-effect';
import moment from 'moment-timezone';
import AppBar from '@mui/material/AppBar';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import {DataGridPro} from '@mui/x-data-grid-pro';

import VisibilityChip from './VisibilityChip';
import DataGridQuickSearchToolbar from './DataGridQuickSearchToolbar';
import {fetchExercises, selectExercises, selectIsLoading as selectIsBuilderLoading} from '../../redux/slices/builder';
import {selectUserGroup, selectUser, selectIsLoading as selectIsUserLoading} from '../../redux/slices/user';
import {BluePillButton} from '../../common/components/BluePillButton';
import {FEATURES, APP_READING, APP_WRITING} from '../../constants';

import baseStyles from '../../css/Base.module.css';

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

export default function SelectMyExerciseDialog({isOpen, handleClose, exerciseType, onSelectExercise}) {
  const dispatch = useDispatch();

  const [exercisesClone, setExercisesClone] = useState([]); // TODO: Refactor this away
  const [searchText, setSearchText] = useState('');
  const [dataGridToolbarFilterButtonEl, setDataGridToolbarFilterButtonEl] = useState(false);

  const userGroup = useSelector(selectUserGroup);
  const allExercises = useSelector(selectExercises);
  const user = useSelector(selectUser);
  const isBuilderLoading = useSelector(selectIsBuilderLoading);
  const isUserLoading = useSelector(selectIsUserLoading);

  const {
    tier: {features},
  } = userGroup;
  const isContentProducerGroup = features.includes(FEATURES.VISIBILITY_PUBLIC);
  const isLoading = isBuilderLoading || isUserLoading;
  const myExercisesFilter = (exercise) => exercise.type === exerciseType && exercise.userID === user._id;
  const flamingoFilter = (exercise) => exercise.type === exerciseType && exercise.visibility === 'PUBLIC';
  const filterFn = isContentProducerGroup ? flamingoFilter : myExercisesFilter;
  const exercises = allExercises.filter(filterFn).map((exercise) => ({id: exercise._id, ...exercise})); // DataGrid requires unique id property

  // Quick search toolbar BEGIN
  function escapeRegExp(value) {
    return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
  }

  const requestSearch = (searchValue) => {
    setSearchText(searchValue);
    const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');

    const filteredRows = exercises.filter((row) => {
      const topLevelIgnoreFields = ['_id', 'id', 'userGroupID', 'userID', '__v', 'type', 'config'];
      let fieldsToSearch = Object.keys(row).filter((field) => !topLevelIgnoreFields.includes(field));
      const configFields = row?.config ? Object.keys(row.config).map((field) => `config.${field}`) : [];
      fieldsToSearch = fieldsToSearch.concat(configFields);

      return fieldsToSearch.some((field) => {
        let searchValue;
        if (field === 'tags') searchValue = row[field].map((tag) => tag.name).join(',');
        else if (field === 'visibility') searchValue = row[field] === 'USER_GROUP' ? 'Everyone' : row[field];
        else if (field.startsWith('config.') && field !== 'config.sentenceHTML')
          searchValue = row.config[[field.replace('config.', '')]]?.toString() ?? '';
        else searchValue = row[field]?.toString() ?? '';

        return searchRegex.test(searchValue);
      });
    });
    setExercisesClone(filteredRows);
  };
  // Quick search toolbar END

  function onExerciseSelect(exercise) {
    onSelectExercise(exercise);
    handleClose();
    setExercisesClone([]);
  }

  // Changing order can affect columns below!
  // The remove prop is not a DataGrid / Grid column property
  const DEFAULT_COLUMNS = [
    {
      field: 'action',
      type: 'actions',
      hideable: false,
      getActions: (params) => [
        <BluePillButton
          onClick={(event) => onExerciseSelect(params.row)}
          size="small"
          sx={{paddingLeft: '15px', paddingRight: '15px'}}
        >
          Select
        </BluePillButton>,
      ],
    },
    {field: 'name', headerName: 'Name', width: 150},
    {field: 'description', headerName: 'Description', width: 250},
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 150,
      type: 'dateTime',
      valueFormatter: (params) => moment(params.value).format('M/D/YYYY h:mm a'),
    },
    {
      field: 'tags',
      headerName: 'Tags',
      width: 300,
      valueGetter: (params) => params.row.tags.map((tag) => tag.name).join(','),
      renderCell: (params) => {
        const {tags} = params.row;
        return (
          <Stack direction="row" spacing={1} sx={{overflowX: 'auto'}}>
            {tags.map((tag) => (
              <Chip key={tag._id} label={tag.name} />
            ))}
          </Stack>
        );
      },
    },
    {
      field: 'visibility',
      headerName: 'Visibility',
      remove: isContentProducerGroup,
      valueGetter: (params) => (params.row.visibility === 'USER_GROUP' ? 'Everyone' : params.row.visibility),
      renderCell: (params) => <VisibilityChip visibility={params.row.visibility} />,
    },
  ].filter((column) => !column.remove);

  const [defaultColumnsAction, defaultColumnsName, defaultColumnsDescription, ...defaultColumnRest] = DEFAULT_COLUMNS;
  const READING_COLUMNS = [
    defaultColumnsAction,
    {...defaultColumnsName, headerName: 'Book'},
    defaultColumnsDescription,
    {field: 'genre', headerName: 'Genre', width: 85, valueGetter: (params) => params.row.config.genre},
    {field: 'language', headerName: 'Language', width: 75, valueGetter: (params) => params.row.config.language},
    {field: 'levelEIL', headerName: 'EIL', width: 50, valueGetter: (params) => params.row.config.levelEIL},
    {field: 'levelFP', headerName: 'F&P', width: 50, valueGetter: (params) => params.row.config.levelFP},
    {field: 'levelLexile', headerName: 'Lexile', width: 55, valueGetter: (params) => params.row.config.levelLexile},
    {
      field: 'wordCount',
      headerName: 'Words',
      type: 'number',
      width: 55,
      valueGetter: (params) => params.row.config.wordCount,
    },
    ...defaultColumnRest,
  ];
  const WRITING_COLUMNS = [
    defaultColumnsAction,
    {
      field: 'sentence',
      headerName: 'Sentence',
      width: 300,
      valueGetter: (params) => params.row.config.sentencePlainText,
      renderCell: (params) => <span dangerouslySetInnerHTML={{__html: params.row.config.sentenceHTML}} />,
    },
    {...defaultColumnsName, headerName: 'Book'},
    ...defaultColumnRest,
  ];

  useEffect(() => {
    setExercisesClone([]);
    if (isOpen) {
      if (isContentProducerGroup) {
        dispatch(fetchExercises({userGroupID: userGroup._id, type: exerciseType, visibility: 'PUBLIC'}));
      } else {
        dispatch(fetchExercises({userID: user._id, type: exerciseType}));
      }
    }
  }, [dispatch, isOpen, exerciseType, isContentProducerGroup, user._id, userGroup._id]);

  useDeepCompareEffect(() => {
    setExercisesClone(exercises);
  }, [exercises, isBuilderLoading]);

  let columns = DEFAULT_COLUMNS;
  if (exerciseType === APP_READING) columns = READING_COLUMNS;
  else if (exerciseType === APP_WRITING) columns = WRITING_COLUMNS;

  function renderDialogContent() {
    return (
      <>
        {isLoading && (
          <div className={baseStyles['progress-container']}>
            <CircularProgress />
          </div>
        )}

        {!isLoading && exercises?.length > 0 && (
          <div style={{height: 'calc(100vh - 70px)', width: '100%'}}>
            <div style={{display: 'flex', height: '100%'}}>
              <div style={{flexGrow: 1}}>
                <DataGridPro
                  pagination
                  components={{Toolbar: DataGridQuickSearchToolbar}}
                  componentsProps={{
                    toolbar: {
                      value: searchText,
                      onChange: (event) => requestSearch(event.target.value),
                      clearSearch: () => requestSearch(''),
                      setFilterButtonEl: setDataGridToolbarFilterButtonEl,
                    },
                    panel: {
                      anchorEl: dataGridToolbarFilterButtonEl,
                    },
                  }}
                  rows={exercisesClone}
                  columns={columns}
                />
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <Dialog fullScreen open={isOpen} onClose={handleClose} TransitionComponent={Transition}>
      <AppBar sx={{position: 'relative'}} className="bg-dark-blue">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
          <Typography sx={{ml: 2, flex: 1}} variant="h6" component="div">
            Select Exercise
          </Typography>
        </Toolbar>
      </AppBar>

      {renderDialogContent()}
    </Dialog>
  );
}
